import type { PayloadAction } from '@reduxjs/toolkit';
import type {
  IChecklistItem,
  IChecklistItemCompletion,
  ITaskAttachment,
  ITaskStockClaim,
  LocationStatus,
  SiteId,
  TaskId,
  TaskStatus,
  WritableLocationStatus,
} from '@interfaces/IScheduler';
import { checklistBlocksCompletion } from '@components/Pages/SchedulerPage/lib/checklistGate';
import {
  appendTaskLogEntry,
  checkEntry,
  commentEntry,
  locationEntry,
  progressEntry,
  statusEntry,
  type ITaskLogMeta,
} from '@components/Pages/SchedulerPage/lib/taskLog';
import {
  DEFAULT_EXECUTION,
  type ISchedulerPlanTask,
  type IActivity,
} from '@components/Pages/SchedulerPage/lib/taskViewCodec';
import type { ISchedulerState } from './scheduler.state';

/** Fallback log author when a caller omits `by` (Router-free tests). Production
 *  callers pass the acting user's id; the server regenerates the authoritative
 *  log on next load, so only intra-session coalescing depends on this value. */
const DEFAULT_LOG_AUTHOR = 'user';

/** Build the log-entry meta from an execution action's payload. */
function logMeta(payload: { activityId: string; at: string; by?: string }): ITaskLogMeta {
  return { id: payload.activityId, at: payload.at, by: payload.by ?? DEFAULT_LOG_AUTHOR };
}

/**
 * Compose a plan task's checklist DEFINITIONS with the execution facet's
 * completion map into checklist items carrying `completion` — the shape
 * {@link checklistBlocksCompletion} reads. Completion is execution-owned
 * (phase 2); the gate must read it from `activityBySite`, not the plan task
 * (which no longer carries it). Returns `undefined` when the task has no
 * checklist (an ungated task).
 */
function composeChecklistForGate(
  task: ISchedulerPlanTask | undefined,
  exec: IActivity | undefined,
): IChecklistItem[] | undefined {
  if (!task?.checklist) return undefined;
  return task.checklist.map((item) => {
    const completion = exec?.checklistCompletion[item.id];
    return completion ? { ...item, completion } : item;
  });
}

// ── Commit + plan status (2026-09-17 task-commit design) ────────────────
// The overlay's PRESENCE in `activityBySite` is what `committed` means, so a
// commit materialises the record and an uncommit deletes it — nothing carries a
// flag that could drift. The task's STATUS is a PLAN fact and lives on the plan
// task in `bySite`; the overlay only witnesses the flip in its log, mirroring
// the entry the server derives (invariant 9).
export function taskCommitted(
  state: ISchedulerState,
  action: PayloadAction<{ siteId: SiteId; taskId: TaskId; dangerousActivity: boolean; assignee: string | null; trusstorGroupId: string | null }>,
) {
  const { siteId, taskId } = action.payload;
  if (!state.bySite[siteId]?.tasks.some(t => t.id === taskId)) return;
  const siteExecution = (state.activityBySite[siteId] ??= {});
  siteExecution[taskId] = {
    ...DEFAULT_EXECUTION(),
    trusstorGroupId: action.payload.trusstorGroupId,
    assignee: action.payload.assignee,
    dangerousActivity: action.payload.dangerousActivity,
  };
}

export function taskUncommitted(state: ISchedulerState, action: PayloadAction<{ siteId: SiteId; taskId: TaskId }>) {
  const siteExecution = state.activityBySite[action.payload.siteId];
  if (siteExecution) delete siteExecution[action.payload.taskId];
}

/**
 * Set a task's PLAN status — mark done, block, reopen/unblock. A row action,
 * not a Gantt edit: it writes the plan task directly and persists through its
 * own PUT (which bumps the schedule version server-side, so the next poll
 * rebases). Blocking a COMMITTED task is refused, matching the server.
 */
export function taskStatusSet(
  state: ISchedulerState,
  action: PayloadAction<{ siteId: SiteId; taskId: TaskId; status: TaskStatus; activityId?: string; at?: string; by?: string }>,
) {
  const { siteId, taskId, status } = action.payload;
  const site = state.bySite[siteId];
  const task = site?.tasks.find(t => t.id === taskId);
  if (!task || task.status === status) return;
  const executionRecord = state.activityBySite[siteId]?.[taskId];
  if (status === 'blocked' && executionRecord) return; // uncommitted-only state
  if (status === 'done' && checklistBlocksCompletion(composeChecklistForGate(task, executionRecord))) {
    state.ui.lastError = 'requiredFieldsMissing';
    return;
  }
  const from = task.status;
  task.status = status;
  const { activityId, at } = action.payload;
  if (executionRecord && activityId && at) {
    const meta = logMeta({ activityId, at, by: action.payload.by });
    executionRecord.log = appendTaskLogEntry(executionRecord.log, statusEntry(meta, from, status));
  }
}

// Checklist COMPLETION is execution-owned (phase 2): the tick + its evidence
// live in the execution record's `checklistCompletion` map (keyed by item
// id), NOT on the plan checklist item (which keeps DEFINITIONS only). The
// DEFINITION (required-evidence modes) still gates the tick. This is an
// immediate-write surface — the ChecklistExecution component also PUTs the
// change through the granular `checks` endpoint — so it's neither UNDOABLE
// nor SYNCABLE. Appends the OPTIMISTIC `check` log entry the server derives.
export function taskChecklistItemToggled(state: ISchedulerState, action: PayloadAction<{ siteId: SiteId; taskId: TaskId; itemId: string; checked: boolean; completion?: IChecklistItemCompletion; activityId: string; at: string; by?: string }>) {
  const { siteId, taskId, itemId } = action.payload;
  const site = state.bySite[siteId];
  if (!site) return;
  const task = site.tasks.find(t => t.id === taskId);
  if (!task) return;
  const item = task.checklist?.find(i => i.id === itemId);
  if (!item) return;

  const siteExecution = (state.activityBySite[siteId] ??= {});
  const executionRecord = (siteExecution[taskId] ??= DEFAULT_EXECUTION());

  if (action.payload.checked) {
    const c = action.payload.completion;
    if (!c) { state.ui.lastError = 'requiredFieldsMissing'; return; }
    if (item.photo      === 'required' && !c.attachedPhoto) { state.ui.lastError = 'requiredFieldsMissing'; return; }
    if (item.comment    === 'required' && !c.comment)        { state.ui.lastError = 'requiredFieldsMissing'; return; }
    if (item.blackboard === 'required' && !c.blackboard)     { state.ui.lastError = 'requiredFieldsMissing'; return; }
    executionRecord.checklistCompletion[itemId] = { ...c, checked: true };
    executionRecord.log = appendTaskLogEntry(executionRecord.log, checkEntry(logMeta(action.payload), itemId, true));
  } else {
    // Un-complete: drop the completion (server `$unset`s the map key), so the
    // gate reads the item as unchecked again.
    delete executionRecord.checklistCompletion[itemId];
    executionRecord.log = appendTaskLogEntry(executionRecord.log, checkEntry(logMeta(action.payload), itemId, false));
  }
}

// Comments are execution-owned (phase 2): a comment is conversation about
// doing the work. Stored on the execution record's `comments` array (not the
// plan task), composed onto the wire `task.comments` at read time.
// Immediate-write (the Comments tab also POSTs to the granular `comments`
// endpoint) — neither UNDOABLE nor SYNCABLE. Appends the optimistic `comment`
// log entry the server derives.
export function taskCommentPosted(state: ISchedulerState, action: PayloadAction<{ siteId: SiteId; taskId: TaskId; commentId: string; at: string; authorName: string; authorId?: string; text: string; attachments: ITaskAttachment[]; by?: string }>) {
  const { siteId, taskId } = action.payload;
  const site = state.bySite[siteId];
  if (!site?.tasks.some(t => t.id === taskId)) return;
  const siteExecution = (state.activityBySite[siteId] ??= {});
  const executionRecord = (siteExecution[taskId] ??= DEFAULT_EXECUTION());
  if (executionRecord.comments.some(c => c.id === action.payload.commentId)) return; // idempotent
  executionRecord.comments.push({
    id: action.payload.commentId,
    at: action.payload.at,
    authorName: action.payload.authorName,
    authorId: action.payload.authorId,
    text: action.payload.text,
    attachments: action.payload.attachments,
  });
  const by = action.payload.by ?? action.payload.authorId ?? DEFAULT_LOG_AUTHOR;
  executionRecord.log = appendTaskLogEntry(
    executionRecord.log,
    commentEntry({ id: `act-${action.payload.commentId}`, at: action.payload.at, by }, action.payload.commentId),
  );
}

// ── Per-task locations (areas) ──────────────────────────────────────────
// Area ASSIGNMENT is a PLAN fact since the task-commit design: it must exist
// before a commit (it is one of its preconditions) and it is what the commit
// hands Trusstor as the activity's areas, so it lives on the plan task. The
// drawer saves each change through the plan auto-save (2026-09-23 amendment).
//
// Area STATUS is Trusstor's. The two values D-ORCA may write (`complete`,
// `readyForReview`) go through their own write-through endpoint; the reducer
// below only mirrors the entry the server logs, because there is no local copy
// of the status to change.
function planLocations(state: ISchedulerState, siteId: SiteId, taskId: TaskId) {
  const task = state.bySite[siteId]?.tasks.find(t => t.id === taskId);
  if (!task) return undefined;
  return (task.locations ??= []);
}

export function taskLocationAdded(state: ISchedulerState, action: PayloadAction<{ siteId: SiteId; taskId: TaskId; locationId: string }>) {
  const locations = planLocations(state, action.payload.siteId, action.payload.taskId);
  if (!locations || locations.some(l => l.locationId === action.payload.locationId)) return;
  locations.push({ locationId: action.payload.locationId, status: 'unplanned' });
}

export function taskLocationRemoved(state: ISchedulerState, action: PayloadAction<{ siteId: SiteId; taskId: TaskId; locationId: string }>) {
  const task = state.bySite[action.payload.siteId]?.tasks.find(t => t.id === action.payload.taskId);
  if (!task?.locations) return;
  task.locations = task.locations.filter(l => l.locationId !== action.payload.locationId);
}

/**
 * Add and/or remove several areas in one shot — backs the Locations tab's
 * drag-to-select (range) gesture so a whole drag is a SINGLE undo entry rather
 * than one per zone. Existing assignments are kept (only genuinely-new ids are
 * appended), so a drag over already-assigned zones never resets their status.
 */
export function taskLocationsBulkToggled(
  state: ISchedulerState,
  action: PayloadAction<{ siteId: SiteId; taskId: TaskId; add: string[]; remove: string[] }>,
) {
  const task = state.bySite[action.payload.siteId]?.tasks.find(t => t.id === action.payload.taskId);
  if (!task) return;
  const removeSet = new Set(action.payload.remove);
  const next = (task.locations ?? []).filter(l => !removeSet.has(l.locationId));
  const present = new Set(next.map(l => l.locationId));
  for (const locationId of action.payload.add) {
    if (!present.has(locationId)) {
      next.push({ locationId, status: 'unplanned' });
      present.add(locationId);
    }
  }
  task.locations = next;
}

/**
 * Mirror a per-area status write: the authoritative copy is Trusstor's area
 * sequence item, so this moves the composed status optimistically (until the
 * next load re-reads it) and appends the log entry the server derives.
 */
export function taskLocationStatusChanged(state: ISchedulerState, action: PayloadAction<{ siteId: SiteId; taskId: TaskId; locationId: string; status: WritableLocationStatus; activityId?: string; at?: string; by?: string }>) {
  const { siteId, taskId } = action.payload;
  const location = state.bySite[siteId]?.tasks
    .find(t => t.id === taskId)?.locations
    ?.find(l => l.locationId === action.payload.locationId);
  if (!location) return;
  const previousStatus = location.status;
  location.status = action.payload.status;
  delete location.progressPercent;
  const executionRecord = state.activityBySite[siteId]?.[taskId];
  const { activityId, at } = action.payload;
  if (executionRecord && activityId && at) {
    const meta = logMeta({ activityId, at, by: action.payload.by });
    executionRecord.log = appendTaskLogEntry(
      executionRecord.log,
      locationEntry(meta, action.payload.locationId, previousStatus, action.payload.status),
    );
  }
}

/**
 * Mirror TRUSSTOR's per-area statuses onto the plan copy the board reads.
 *
 * Per-area status LIVES in Trusstor (the area sequence item), and the schedule
 * read is deliberately Trusstor-free so a 502 there can't take the Gantt down —
 * which means every committed area arrives from that read as plain `planned`.
 * The page joins the committed-activities feed client-side instead, and this is
 * where the join lands: an area a foreman completed in Trusstor shows as done
 * here, and so does anything the IoT computed (in progress, delayed, overdue).
 *
 * OBSERVATION, not an edit: it is in none of UNDOABLE / SYNCABLE / FREEZABLE —
 * nothing to undo, nothing to save, and upstream truth must land even while the
 * site is stale. Only areas the feed actually reports are touched, so a task it
 * does not cover keeps whatever D-ORCA last wrote optimistically.
 *
 * `workTimeMs` rides along: the tag work time the IoT has counted per area,
 * shown beside the area on the board. Same observation semantics — landed
 * where reported, left alone where not.
 */
export function areaStatusesObserved(
  state: ISchedulerState,
  action: PayloadAction<{
    siteId: SiteId;
    byTask: Record<TaskId, Record<string, LocationStatus>>;
    workTimeMs?: Record<TaskId, Record<string, number>>;
  }>,
) {
  const site = state.bySite[action.payload.siteId];
  if (!site) return;
  for (const task of site.tasks) {
    const observed = action.payload.byTask[task.id];
    if (!observed || !task.locations) continue;
    const workTime = action.payload.workTimeMs?.[task.id];
    for (const location of task.locations) {
      const status = observed[location.locationId];
      if (status && status !== location.status) location.status = status;
      const ms = workTime?.[location.locationId];
      if (ms !== undefined && ms !== location.workTimeMs) location.workTimeMs = ms;
    }
  }
}

/**
 * Set a leaf task's progress percentage directly (0–100, clamped + rounded).
 * Backs the Overview "edit section" (slider + area board). The slider's
 * manual freeze is SESSION state inside TaskProgressSection — nothing about
 * it is stored; this reducer just writes the resulting number into the
 * task's execution record. Immediate-write (persisted by its own PUT), and
 * NOT undoable — a slider drag would otherwise flood the history with one
 * entry per tick (same treatment as journal-shaped writes).
 */
export function taskProgressSet(state: ISchedulerState, action: PayloadAction<{ siteId: SiteId; taskId: TaskId; progressPercent: number; activityId?: string; at?: string; by?: string }>) {
  const { siteId, taskId } = action.payload;
  if (!state.bySite[siteId]?.tasks.some(t => t.id === taskId)) return;
  // Progress is an EXECUTION fact: only a COMMITTED task has one, and the
  // server refuses the write for any other. No record ⇒ nothing to move.
  const executionRecord = state.activityBySite[siteId]?.[taskId];
  if (!executionRecord) return;
  const next = Math.max(0, Math.min(100, Math.round(action.payload.progressPercent)));
  if (executionRecord.progressPercent === next) return;
  const fromProgress = executionRecord.progressPercent;
  executionRecord.progressPercent = next;
  // Optimistic mirror of the server-derived `progress` entry. Appended only
  // when the caller supplies log meta (production always does; Router-free
  // tests may omit it and just move the number).
  const { activityId, at } = action.payload;
  if (activityId && at) {
    const meta = logMeta({ activityId, at, by: action.payload.by });
    executionRecord.log = appendTaskLogEntry(executionRecord.log, progressEntry(meta, fromProgress, next));
  }
}

// ── Stock claims ("Stock from CAD") — execution facet ───────────────────
// A material claim records what the work consumes, so it lives in the per-task
// activities document, not the plan (ruling 9, 2026-09-09). The drawer edits
// the set in its local draft and commits it at Save / delivery-Create through
// `useTaskStockClaimsPersist` — a whole-array replace, last write wins, on the
// same immediate-write channel as progress: neither UNDOABLE nor SYNCABLE,
// frozen on a stale base. No optimistic log entry (the server writes none for
// this mutation). Like every other execution write it needs an overlay to land
// on — an uncommitted task has no activity to consume anything (invariant 2).
export function taskStockClaimsSet(
  state: ISchedulerState,
  action: PayloadAction<{ siteId: SiteId; taskId: TaskId; stockClaims: ITaskStockClaim[] }>,
) {
  const { siteId, taskId } = action.payload;
  const executionRecord = state.activityBySite[siteId]?.[taskId];
  if (!executionRecord) return; // uncommitted: the server refuses the write
  executionRecord.stockClaims = action.payload.stockClaims;
}
